// ARG Terminal - Hidden puzzle game
// Accessible via LOGIN command on BBS guestbook

interface ARGStage {
  id: number;
  prompt: string;
  hint?: string;
  answers: string[];
  onCorrect: () => string; // Returns the next prompt/content
}

// Puzzle stages
const STAGES: ARGStage[] = [
  {
    id: 1,
    prompt: 'minimal',
    answers: ['need'],
    onCorrect: () => `
> LOCATION RECOVERED:
  https://www.google.com/local/place/fid/0x8841b386e5a3a045:0xfbff9d299db0eadc/photosphere?iu=https://streetviewpixels-pa.googleapis.com/v1/thumbnail?panoid%3DHElsR68PGPjZ40xvBy4Uyw%26cb_client%3Dsearch.gws-prod.gps%26yaw%3D118.17848%26pitch%3D0%26thumbfov%3D100%26w%3D0%26h%3D0&ik=CAISFkhFbHNSNjhQR1BqWjQweHZCeTRVeXc%3D

> AWAITING OBSERVATION...`
  },
  {
    id: 2,
    prompt: '',
    answers: ['coney'],
    onCorrect: () => `
> DECRYPTING NEXT COORDINATES...

  Lpsury Flqflqqdwl Oxgorz Dyhqxh

> [CIPHER: SHIFT-3]`
  },
  {
    id: 3,
    prompt: '',
    answers: ['improv cincinnati ludlow avenue', 'improv cincinnati ludlow ave', 'improv cincinnati', 'improv ludlow'],
    onCorrect: () => `
> LOCATION VERIFIED:
  https://www.google.com/local/place/fid/0x8841b49251f20dbd:0x5dce79ced1add5fe/photosphere?iu=https://streetviewpixels-pa.googleapis.com/v1/thumbnail?panoid%3DX34EA_uAGS8DX2H7MBnyYQ%26cb_client%3Dsearch.gws-prod.gps%26yaw%3D344.15836%26pitch%3D0%26thumbfov%3D100%26w%3D0%26h%3D0&ik=CAISFlgzNEVBX3VBR1M4RFgySDdNQm55WVE%3D

> AWAITING OBSERVATION...`
  },
  {
    id: 4,
    prompt: '',
    answers: ['sitwells', "sitwell's", 'sitwell'],
    onCorrect: () => `
> FINAL VECTOR SEQUENCE:

  dim(observation) = [0.391312, -0.845094, 0.00, 0.00]
  // geographic embedding space
  // standard scale: 10^2`
  },
  {
    id: 5,
    prompt: '',
    answers: ['39.1312, -84.5094', '39.1312 -84.5094', '39.1312,-84.5094', '39.131, -84.509', '39.131 -84.509', '39.131,-84.509'],
    onCorrect: () => `
> LOCATION VERIFIED:
  https://www.google.com/maps/place/Papa+Johns+Pizza/@39.1312301,-84.509414,3a,75y,274.91h,98.34t/data=!3m7!1e1!3m5!1sni6iRWka9pFDzyblsZNBkA!2e0!6shttps:%2F%2Fstreetviewpixels-pa.googleapis.com%2Fv1%2Fthumbnail%3Fcb_client%3Dmaps_sv.tactile%26w%3D900%26h%3D600%26pitch%3D-8.342141644455694%26panoid%3Dni6iRWka9pFDzyblsZNBkA%26yaw%3D274.914470804266!7i16384!8i8192!4m10!1m2!2m1!1spapa+johns!3m6!1s0x8841b392e4df147b:0xc444b78e7f6f4077!8m2!3d39.1312056!4d-84.5096462

> AWAITING FINAL OBSERVATION...`
  },
  {
    id: 6,
    prompt: '',
    answers: ['vomit'],
    onCorrect: () => 'ACCESS_GRANTED'
  }
];

// Track current state
let currentStage = 0;
let terminalElement: HTMLElement | null = null;
let inputElement: HTMLInputElement | null = null;
let outputElement: HTMLElement | null = null;
let isActive = false;

/**
 * Initialize and show the ARG terminal
 */
export function showARGTerminal(): void {
  if (isActive) return;
  isActive = true;
  currentStage = 0;

  // Check if user has already completed the ARG
  const completed = localStorage.getItem('chopras_arg_complete');

  createTerminalOverlay();

  if (completed) {
    showCompletedState();
  } else {
    showStage(0);
  }
}

/**
 * Hide and cleanup the ARG terminal
 */
export function hideARGTerminal(): void {
  if (terminalElement) {
    terminalElement.classList.add('arg-terminal--closing');
    setTimeout(() => {
      terminalElement?.remove();
      terminalElement = null;
      inputElement = null;
      outputElement = null;
      isActive = false;
    }, 300);
  }
}

/**
 * Create the terminal overlay UI
 */
function createTerminalOverlay(): void {
  const overlay = document.createElement('div');
  overlay.className = 'arg-terminal-overlay';
  overlay.innerHTML = `
    <div class="arg-terminal">
      <div class="arg-terminal__header">
        <span class="arg-terminal__title">TERMINAL v0.1 - RESTRICTED ACCESS</span>
        <button class="arg-terminal__close" aria-label="Close terminal">[X]</button>
      </div>
      <div class="arg-terminal__body">
        <div class="arg-terminal__output"></div>
        <div class="arg-terminal__input-line">
          <span class="arg-terminal__prompt">></span>
          <input
            type="text"
            class="arg-terminal__input"
            autocomplete="off"
            autocapitalize="off"
            spellcheck="false"
            aria-label="Terminal input"
          />
        </div>
      </div>
    </div>
  `;

  document.body.appendChild(overlay);
  terminalElement = overlay;
  outputElement = overlay.querySelector('.arg-terminal__output');
  inputElement = overlay.querySelector('.arg-terminal__input');

  // Focus input
  setTimeout(() => inputElement?.focus(), 100);

  // Event listeners
  const closeBtn = overlay.querySelector('.arg-terminal__close');
  closeBtn?.addEventListener('click', hideARGTerminal);

  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) {
      hideARGTerminal();
    }
  });

  inputElement?.addEventListener('keydown', handleInput);

  // Escape to close
  document.addEventListener('keydown', handleEscape);
}

/**
 * Handle escape key
 */
function handleEscape(e: KeyboardEvent): void {
  if (e.key === 'Escape' && isActive) {
    hideARGTerminal();
    document.removeEventListener('keydown', handleEscape);
  }
}

/**
 * Handle terminal input
 */
function handleInput(e: KeyboardEvent): void {
  if (e.key !== 'Enter' || !inputElement) return;

  const input = inputElement.value.trim().toLowerCase();
  if (!input) return;

  // Add input to output
  appendOutput(`> ${inputElement.value}`);
  inputElement.value = '';

  // Check answer
  const stage = STAGES[currentStage];
  if (stage && stage.answers.includes(input)) {
    // Correct answer
    const result = stage.onCorrect();

    if (result === 'ACCESS_GRANTED') {
      showAccessGranted();
    } else {
      appendOutput(result);
      currentStage++;
    }
  } else {
    // Wrong answer
    appendOutput('> [INVALID INPUT]');

    // Easter egg responses
    if (input === 'help') {
      appendOutput('> No help available. Trust your observations.');
    } else if (input === 'quit' || input === 'exit') {
      appendOutput('> Leaving so soon?');
      setTimeout(hideARGTerminal, 1000);
    } else if (input === 'carlos') {
      appendOutput('> Who?');
    } else if (input === 'wesleyan') {
      appendOutput('> You remember.');
    }
  }
}

/**
 * Show a specific puzzle stage
 */
function showStage(stageIndex: number): void {
  const stage = STAGES[stageIndex];
  if (!stage) return;

  if (stageIndex === 0) {
    appendOutput(`
> CONNECTION ESTABLISHED
> AUTHENTICATION REQUIRED

> ${stage.prompt}`);
  }
}

/**
 * Append text to terminal output
 */
function appendOutput(text: string): void {
  if (!outputElement) return;

  const lines = text.split('\n');
  lines.forEach(line => {
    const lineEl = document.createElement('div');
    lineEl.className = 'arg-terminal__line';

    // Make URLs clickable
    if (line.includes('https://')) {
      const urlMatch = line.match(/(https:\/\/[^\s]+)/);
      if (urlMatch) {
        const url = urlMatch[1];
        const beforeUrl = line.substring(0, line.indexOf(url));
        const afterUrl = line.substring(line.indexOf(url) + url.length);

        lineEl.innerHTML = `${escapeHtml(beforeUrl)}<a href="${escapeHtml(url)}" target="_blank" rel="noopener" class="arg-terminal__link">${escapeHtml(url)}</a>${escapeHtml(afterUrl)}`;
      } else {
        lineEl.textContent = line;
      }
    } else {
      lineEl.textContent = line;
    }

    outputElement?.appendChild(lineEl);
  });

  // Scroll to bottom
  outputElement.scrollTop = outputElement.scrollHeight;
}

/**
 * Show access granted and download links
 */
function showAccessGranted(): void {
  // Mark as complete
  localStorage.setItem('chopras_arg_complete', 'true');

  appendOutput(`
> ACCESS GRANTED
> ═══════════════════════════════════════════════════════

  He wanted you to have this.

`);

  // Create download links
  const downloadSection = document.createElement('div');
  downloadSection.className = 'arg-terminal__downloads';
  downloadSection.innerHTML = `
    <a href="/assets/books/house-of-chopras.pdf" download class="arg-terminal__download">
      [DOWNLOAD: PDF]
    </a>
    <a href="/assets/books/house-of-chopras.epub" download class="arg-terminal__download">
      [DOWNLOAD: EPUB]
    </a>
  `;
  outputElement?.appendChild(downloadSection);

  appendOutput(`
> ═══════════════════════════════════════════════════════
> SESSION COMPLETE
> The manuscript is yours now.
`);

  // Disable input
  if (inputElement) {
    inputElement.disabled = true;
    inputElement.placeholder = '[SESSION ENDED]';
  }
}

/**
 * Show completed state for returning users
 */
function showCompletedState(): void {
  appendOutput(`
> CONNECTION ESTABLISHED
> SESSION PREVIOUSLY COMPLETED
> ═══════════════════════════════════════════════════════

  He wanted you to have this.

`);

  const downloadSection = document.createElement('div');
  downloadSection.className = 'arg-terminal__downloads';
  downloadSection.innerHTML = `
    <a href="/assets/books/house-of-chopras.pdf" download class="arg-terminal__download">
      [DOWNLOAD: PDF]
    </a>
    <a href="/assets/books/house-of-chopras.epub" download class="arg-terminal__download">
      [DOWNLOAD: EPUB]
    </a>
  `;
  outputElement?.appendChild(downloadSection);

  appendOutput(`
> ═══════════════════════════════════════════════════════
> The manuscript remains yours.
`);

  if (inputElement) {
    inputElement.disabled = true;
    inputElement.placeholder = '[SESSION COMPLETE]';
  }
}

/**
 * Escape HTML to prevent XSS
 */
function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

/**
 * Check if user has completed the ARG
 */
export function hasCompletedARG(): boolean {
  return localStorage.getItem('chopras_arg_complete') === 'true';
}

/**
 * Reset ARG progress (for testing)
 */
export function resetARGProgress(): void {
  localStorage.removeItem('chopras_arg_complete');
}
